import {getApiHeaders} from '../../auth';
import {config, getApiBaseUrl} from '../../../config/app-config';
import {apiPut} from '../../../utils/api';
import { getBaseType } from '../block-configs';
import {showErrorNotification, showSuccessNotification} from '../../../utils/notifications';
import {addPendingBlock, hasChanges, updateBlockOrderData} from './block-state';

// Function to create unique section identifier
function getSectionUniqueId(sectionElement) {
    if (!sectionElement) return null;

    const sectionName = sectionElement.getAttribute('data-section');
    const sectionClasses = sectionElement.className || '';

    return `${sectionName}:${sectionClasses}`;
}

const sortableState = {
    initialized: false,
    sections: new Map(),
    draggedElement: null,
    dragoverElement: null,
    dragStartY: 0,
    originalOrder: [],
    blockOrders: [],
    hasReordered: false,
    dragPosition: null // 'before' or 'after'
};

export function initSortable(sectionElement) {
    if (!sectionElement) {
        return;
    }

    const sectionName = sectionElement.getAttribute('data-section');
    if (!sectionName) {
        return;
    }

    // Create unique identifier with classes
    const uniqueSectionId = getSectionUniqueId(sectionElement);

    if (sortableState.sections.has(uniqueSectionId)) {
        return;
    }

    const blocks = Array.from(sectionElement.children).filter(el =>
        el.hasAttribute('data-block-name')
    );

    if (!blocks.length) {
        return;
    }

    sortableState.sections.set(uniqueSectionId, {
        element: sectionElement,
        sectionName: sectionName,
        sectionClasses: sectionElement.className || '',
        blocks: new Map()
    });

    blocks.forEach((block, index) => {
        initDraggableBlock(block, uniqueSectionId, index);
    });

    try {
        sectionElement.addEventListener('dragover', handleSectionDragOver);
        sectionElement.addEventListener('dragenter', handleSectionDragEnter);
        sectionElement.addEventListener('dragleave', handleSectionDragLeave);
        sectionElement.addEventListener('drop', handleSectionDrop);
    } catch (error) {
        console.error('Error adding event handlers:', error);
    }

    sortableState.initialized = true;
}

export function initDraggableBlock(blockElement, uniqueSectionId, initialOrder) {
    if (!blockElement) {
        return;
    }

    const blockId = blockElement.getAttribute('data-block-id');
    const blockName = blockElement.getAttribute('data-block-name');

    let order = initialOrder;
    if (blockElement.hasAttribute('data-order')) {
        const dataOrderStr = blockElement.getAttribute('data-order');
        if (dataOrderStr !== null && dataOrderStr !== undefined) {
            const dataOrder = parseInt(dataOrderStr, 10);
            if (!isNaN(dataOrder)) {
                order = dataOrder;
            }
        }
    }

    try {
        addDragHandle(blockElement);
    } catch (error) {
        console.error('Error adding drag handle:', error);
    }

    try {
        blockElement.setAttribute('draggable', 'true');
        blockElement.setAttribute('data-order', String(order));
    } catch (error) {
        console.error('Error setting attributes on block:', error);
    }

    const sectionInfo = sortableState.sections.get(uniqueSectionId);
    if (sectionInfo) {
        try {
            sectionInfo.blocks.set(blockId || blockName, {
                element: blockElement,
                id: blockId || blockName,
                name: blockName,
                order: order
            });
        } catch (error) {
            console.error('Error adding block to state:', error);
        }
    }

    try {
        blockElement.addEventListener('dragstart', handleDragStart);
        blockElement.addEventListener('dragend', handleDragEnd);
        blockElement.addEventListener('dragover', handleBlockDragOver);
        blockElement.addEventListener('dragleave', handleBlockDragLeave);
    } catch (error) {
        console.error('Error adding event handlers to block:', error);
    }
}

function addDragHandle(blockElement) {
    if (blockElement.querySelector('.block-drag-handle')) {
        return;
    }

    const handle = document.createElement('div');
    handle.className = 'block-drag-handle';
    handle.title = 'Drag to reorder';
    handle.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="8" y1="6" x2="21" y2="6"></line>
      <line x1="8" y1="12" x2="21" y2="12"></line>
      <line x1="8" y1="18" x2="21" y2="18"></line>
      <line x1="3" y1="6" x2="3.01" y2="6"></line>
      <line x1="3" y1="12" x2="3.01" y2="12"></line>
      <line x1="3" y1="18" x2="3.01" y2="18"></line>
    </svg>
  `;

    handle.style.cssText = `
    position: absolute;
    top: 10px;
    left: 10px;
    width: 24px;
    height: 24px;
    cursor: grab;
    z-index: 10;
    opacity: 0.6;
    transition: opacity 0.3s;
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

    handle.addEventListener('mouseover', () => {
        handle.style.opacity = '1';
    });

    handle.addEventListener('mouseout', () => {
        handle.style.opacity = '0.6';
    });

    const computedStyle = getComputedStyle(blockElement);
    if (computedStyle.position === 'static') {
        blockElement.style.position = 'relative';
    }

    try {
        blockElement.appendChild(handle);
    } catch (error) {
        console.error('Error adding handle to block:', error);
    }
}

function handleDragStart(event) {
    sortableState.draggedElement = event.currentTarget;

    try {
        event.dataTransfer.effectAllowed = 'move';
    } catch (error) {
        console.error('Error setting drag effect:', error);
    }

    sortableState.dragStartY = event.clientY ||
        (event.touches && event.touches[0] ? event.touches[0].clientY : 0);

    const sectionElement = findParentSection(sortableState.draggedElement);
    if (sectionElement) {
        const uniqueSectionId = getSectionUniqueId(sectionElement);
        const sectionInfo = sortableState.sections.get(uniqueSectionId);

        if (sectionInfo) {
            try {
                sortableState.originalOrder = Array.from(sectionInfo.blocks.values())
                    .sort((a, b) => a.order - b.order)
                    .map(block => ({id: block.id, order: block.order}));
            } catch (error) {
                console.error('Error saving original order:', error);
            }
        }
    }

    try {
        sortableState.draggedElement.classList.add('dragging');
    } catch (error) {
        console.error('Error adding dragging class:', error);
    }

    try {
        const dragImage = sortableState.draggedElement.cloneNode(true);
        dragImage.style.opacity = '0.7';
        dragImage.style.position = 'absolute';
        dragImage.style.top = '-1000px';
        document.body.appendChild(dragImage);

        event.dataTransfer.setDragImage(dragImage, 10, 10);

        setTimeout(() => {
            document.body.removeChild(dragImage);
        }, 0);
    } catch (error) {
        console.error('Error creating drag image:', error);
    }
}

function handleDragEnd(event) {
    if (!sortableState.draggedElement) {
        return;
    }

    try {
        sortableState.draggedElement.classList.remove('dragging');
    } catch (error) {
        console.error('Error removing dragging class:', error);
    }

    const sectionElement = findParentSection(sortableState.draggedElement);
    if (sectionElement) {
        try {
            const blocks = sectionElement.querySelectorAll('[data-block-name]');
            blocks.forEach(block => {
                block.classList.remove('drag-over');
                block.classList.remove('drag-over-top');
                block.classList.remove('drag-over-bottom');
            });
        } catch (error) {
            console.error('Error removing drag indicators:', error);
        }
    }

    if (sortableState.hasReordered) {
        const sectionElement = findParentSection(sortableState.draggedElement);
        if (sectionElement) {
            const uniqueSectionId = getSectionUniqueId(sectionElement);
            try {
                prepareOrderForSaving(uniqueSectionId);
            } catch (error) {
                console.error('Error preparing order for saving:', error);
            }
        }
    }

    sortableState.draggedElement = null;
    sortableState.dragoverElement = null;
    sortableState.dragPosition = null;
    sortableState.hasReordered = false;
}

function handleBlockDragOver(event) {
    if (!sortableState.draggedElement || event.currentTarget === sortableState.draggedElement) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    const block = event.currentTarget;
    const rect = block.getBoundingClientRect();
    const mouseY = event.clientY;
    const relativePosition = mouseY - rect.top;
    const isInTopHalf = relativePosition < rect.height / 2;

    // Remove other indicators first
    block.classList.remove('drag-over');
    block.classList.remove('drag-over-top');
    block.classList.remove('drag-over-bottom');

    // Add appropriate class based on position
    if (isInTopHalf) {
        block.classList.add('drag-over-top');
        sortableState.dragPosition = 'before';
    } else {
        block.classList.add('drag-over-bottom');
        sortableState.dragPosition = 'after';
    }

    sortableState.dragoverElement = block;
}

function handleBlockDragLeave(event) {
    const block = event.currentTarget;
    block.classList.remove('drag-over-top');
    block.classList.remove('drag-over-bottom');
}

function handleSectionDragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
}

function handleSectionDragEnter(event) {
    event.preventDefault();
}

function handleSectionDragLeave(event) {
    // Only clear if leaving the section, not just moving between blocks
    if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) {
        if (sortableState.dragoverElement) {
            sortableState.dragoverElement.classList.remove('drag-over-top');
            sortableState.dragoverElement.classList.remove('drag-over-bottom');
            sortableState.dragoverElement = null;
        }
    }
}

function handleSectionDrop(event) {
    event.preventDefault();

    if (!sortableState.draggedElement || !sortableState.dragoverElement) {
        return;
    }

    const draggedBlock = sortableState.draggedElement;
    const targetBlock = sortableState.dragoverElement;
    const position = sortableState.dragPosition;

    if (draggedBlock === targetBlock) {
        return;
    }

    const draggedBlockSection = findParentSection(draggedBlock);
    const targetBlockSection = findParentSection(targetBlock);

    if (!draggedBlockSection || !targetBlockSection) {
        return;
    }

    // Check that blocks are in the same section
    if (draggedBlockSection !== targetBlockSection) {
        showErrorNotification('Drag and drop between different sections is not supported');
        return;
    }

    // Use unique identifier to find section info
    const uniqueSectionId = getSectionUniqueId(draggedBlockSection);
    const sectionInfo = sortableState.sections.get(uniqueSectionId);
    if (!sectionInfo) {
        return;
    }

    try {
        // Insert based on position
        if (position === 'before') {
            draggedBlockSection.insertBefore(draggedBlock, targetBlock);
        } else {
            draggedBlockSection.insertBefore(draggedBlock, targetBlock.nextSibling);
        }
    } catch (error) {
        console.error('Error moving block in DOM:', error);
    }

    try {
        updateBlockOrder(sectionInfo);
    } catch (error) {
        console.error('Error updating block order:', error);
    }

    sortableState.hasReordered = true;

    // Clear indicators
    targetBlock.classList.remove('drag-over-top');
    targetBlock.classList.remove('drag-over-bottom');
    sortableState.dragoverElement = null;

    try {
        // Mark blocks as reordered for visual feedback
        draggedBlock.classList.add('block-reordered');

        setTimeout(() => {
            draggedBlock.classList.remove('block-reordered');
        }, 1000);
    } catch (error) {
        console.error('Error adding visual feedback:', error);
    }

    try {
        // Generate block order change event
        const orderChangedEvent = new CustomEvent('blockOrderChanged', {
            detail: {
                section: draggedBlockSection,
                blocks: sortableState.blockOrders
            }
        });
        document.dispatchEvent(orderChangedEvent);
    } catch (error) {
        console.error('Error generating blockOrderChanged event:', error);
    }
}

// Updated function to find parent section that considers class
function findParentSection(blockElement) {
    if (!blockElement) return null;

    let parent = blockElement.parentElement;
    let depth = 0;
    const maxDepth = 10; // Prevent infinite loop

    while (parent && depth < maxDepth) {
        depth++;
        if (parent.hasAttribute('data-section')) {
            return parent;
        }
        parent = parent.parentElement;
    }

    return null;
}

function updateBlockOrder(sectionInfo) {
    const uniqueSectionId = getSectionUniqueId(sectionInfo.element);

    if (!sectionInfo || !sectionInfo.element) {
        return;
    }

    try {
        const blocks = Array.from(sectionInfo.element.querySelectorAll('[data-block-name]'));

        blocks.forEach((block, index) => {
            const blockId = block.getAttribute('data-block-id');
            const blockName = block.getAttribute('data-block-name');
            const id = blockId || blockName;

            const blockInfo = sectionInfo.blocks.get(id);

            if (blockInfo) {
                // Update order in data
                blockInfo.order = index;

                // Update data-order attribute of element
                block.setAttribute('data-order', String(index));

                // НОВАЯ ЛОГИКА: Специальная обработка для модулей курса
                if (blockName && blockName.includes('course-module-')) {
                    updateCourseModuleNumber(block, index);
                }

                // Check if section is accordion
                if (sectionInfo.element.classList.contains('accordion-cases')) {
                    const displayNumber = index + 1; // Start from 1, not 0

                    // Update number in header
                    const headerNumber = block.querySelector('.accordion-header .case-number');
                    if (headerNumber) {
                        headerNumber.textContent = displayNumber;
                    }

                    // Update number in expanded content
                    const contentNumber = block.querySelector('.accordion-content .case-number');
                    if (contentNumber) {
                        contentNumber.textContent = displayNumber;
                    }
                }

                // Add check for diagnostic-step
                if (block.classList.contains('diagnostic-step-item')) {
                    const displayNumber = index + 1; // Start from 1

                    // Update number in step-number element
                    const stepNumber = block.querySelector('.step-number');
                    if (stepNumber) {
                        stepNumber.textContent = `Step ${displayNumber}`;
                    }
                }
            }
        });

        sortableState.blockOrders = Array.from(sectionInfo.blocks.values())
            .map(block => {
                try {
                    const blockId = block.id;
                    // For newly added blocks without an ID
                    if (!blockId || blockId === block.name) {
                        return null;
                    }

                    return {
                        block_id: blockId,
                        order: block.order
                    };
                } catch (e) {
                    console.error(`Error processing block ID: ${block.id}`, e);
                    return null;
                }
            })
            .filter(item => item !== null);
    } catch (error) {
        console.error('Error updating block order:', error);
    }
}

function updateCourseModuleNumber(moduleBlock, newIndex) {
    const moduleNumber = newIndex + 1; // Модули начинаются с 1

    // Обновляем атрибут
    moduleBlock.setAttribute('data-module-number', String(moduleNumber));

    // Обновляем заголовки
    const desktopHeader = moduleBlock.querySelector('.d-none.d-md-block.head-accordion');
    const mobileHeader = moduleBlock.querySelector('.d-md-none.head-accordion');

    if (desktopHeader) {
        const currentText = desktopHeader.textContent;
        const newText = currentText.replace(/Модуль \d+/, `Модуль ${moduleNumber}`);
        desktopHeader.textContent = newText;
        console.log(`[Sortable] Updated desktop header for module to: ${newText}`);
    }

    if (mobileHeader) {
        const currentText = mobileHeader.textContent;
        const newText = currentText.replace(/Модуль \d+/, `Модуль ${moduleNumber}`);
        mobileHeader.textContent = newText;
        console.log(`[Sortable] Updated mobile header for module to: ${newText}`);
    }

    console.log(`[Sortable] Updated course module ${moduleBlock.getAttribute('data-block-name')} to number ${moduleNumber}`);
}

function prepareOrderForSaving(uniqueSectionId) {
    if (!sortableState.blockOrders.length) {
        return;
    }

    try {
        // Get section by unique ID
        const sectionInfo = sortableState.sections.get(uniqueSectionId);
        if (!sectionInfo || !sectionInfo.element) {
            return;
        }

        const sectionElement = sectionInfo.element;

        // Use element id if exists, otherwise use class
        const sectionId = sectionElement.id || sectionElement.getAttribute('class');

        const requestData = {
            parent_block: sectionId,
            block_orders: sortableState.blockOrders
        };

        // Store the reordering to be published later
        updateBlockOrderData(uniqueSectionId, requestData);

        showSuccessNotification('Block order will be updated after publishing changes');
    } catch (error) {
        console.error('Error preparing order for saving:', error);
    }
}

function restoreOriginalOrder(sectionElement) {
    const uniqueSectionId = getSectionUniqueId(sectionElement);

    if (!sectionElement || !sortableState.originalOrder.length) {
        return;
    }

    const sectionInfo = sortableState.sections.get(uniqueSectionId);

    if (!sectionInfo) {
        return;
    }

    try {
        const sortedBlocks = sortableState.originalOrder
            .map(item => {
                const blockInfo = sectionInfo.blocks.get(item.id.toString());
                return blockInfo ? blockInfo.element : null;
            })
            .filter(Boolean);

        sortedBlocks.forEach(blockElement => {
            sectionElement.appendChild(blockElement);
        });

        updateBlockOrder(sectionInfo);
    } catch (error) {
        console.error('Error restoring original order:', error);
    }
}

async function saveOrderToServer(sectionName, requestData) {
    try {
        await apiPut(config.api.paths.reorderBlocks, requestData);
        showSuccessNotification('Block order successfully updated');
        return true;
    } catch (error) {
        console.error('Error saving block order to server:', error);
        showErrorNotification('Failed to update block order');

        // Restore original order
        let sectionElement = null;

        // First check if sectionName is unique identifier
        for (const [uniqueId, info] of sortableState.sections.entries()) {
            if (uniqueId === sectionName) {
                sectionElement = info.element;
                break;
            }
        }

        // If not found, try to find by data-section attribute
        if (!sectionElement) {
            sectionElement = document.querySelector(`[data-section="${sectionName}"]`);
        }

        if (sectionElement) {
            restoreOriginalOrder(sectionElement);
        }

        return false;
    }
}

export function addDragAndDropStyles() {
    const styleId = 'block-draggable-styles';

    if (document.getElementById(styleId)) {
        return;
    }

    try {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
        [draggable=true] {
          user-select: none;
          -webkit-user-drag: element;
          position: relative;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        
        .dragging {
          opacity: 0.6;
          border: 2px dashed #4285f4 !important;
          transform: scale(0.98);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          z-index: 10;
        }
        
        .drag-over-top {
          border-top: 2px solid #4285f4;
          padding-top: 8px;
          margin-top: -8px;
          position: relative;
        }
        
        .drag-over-top::before {
          content: '';
          position: absolute;
          top: -2px;
          left: 0;
          right: 0;
          height: 2px;
          background-color: #4285f4;
          z-index: 5;
        }
        
        .drag-over-bottom {
          border-bottom: 2px solid #4285f4;
          padding-bottom: 8px;
          margin-bottom: -8px;
          position: relative;
        }
        
        .drag-over-bottom::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 2px;
          background-color: #4285f4;
          z-index: 5;
        }
        
        .block-drag-handle:hover {
          opacity: 1 !important;
          transform: scale(1.1);
        }
        
        .block-drag-handle:active {
          cursor: grabbing;
          cursor: -webkit-grabbing;
        }
        
        .block-drag-handle:active svg {
          transform: scale(0.9);
        }
        
        @keyframes blockHighlight {
          0% { box-shadow: 0 0 0 rgba(66, 133, 244, 0); }
          50% { box-shadow: 0 0 10px rgba(66, 133, 244, 0.5); }
          100% { box-shadow: 0 0 0 rgba(66, 133, 244, 0); }
        }
        
        .block-reordered {
          animation: blockHighlight 1s ease-in-out;
        }
      `;

        document.head.appendChild(style);
    } catch (error) {
        console.error('Error adding drag and drop styles:', error);
    }
}

export function initAllSortables() {
    try {
        addDragAndDropStyles();
    } catch (error) {
        console.error('Error adding styles:', error);
    }

    try {
        const sections = document.querySelectorAll('[data-section]');

        sections.forEach((section, index) => {
            const uniqueSectionId = getSectionUniqueId(section);
            initSortable(section);
        });
    } catch (error) {
        console.error('Error initializing all sections:', error);
    }
}

export function makeBlockDraggable(blockElement) {
    if (!blockElement) {
        return;
    }

    try {
        const sectionElement = findParentSection(blockElement);
        if (!sectionElement) {
            return;
        }

        const sectionName = sectionElement.getAttribute('data-section');
        if (!sectionName) {
            return;
        }

        // Use unique section identifier
        const uniqueSectionId = getSectionUniqueId(sectionElement);

        let sectionInfo = sortableState.sections.get(uniqueSectionId);
        if (!sectionInfo) {
            initSortable(sectionElement);
            sectionInfo = sortableState.sections.get(uniqueSectionId);
        }

        if (!sectionInfo) {
            return;
        }

        try {
            const blockOrders = Array.from(sectionInfo.blocks.values()).map(block => block.order);
            const lastOrder = blockOrders.length ? Math.max(...blockOrders) + 1 : 0;

            initDraggableBlock(blockElement, uniqueSectionId, lastOrder);
        } catch (error) {
            console.error('Error determining last order number:', error);
        }
    } catch (error) {
        console.error('Error setting up block for dragging:', error);
    }
}

// Export function to publish block orders from block-publisher.js
export function publishBlockOrders() {
    try {
        const orderData = window.blockOrderData || {};
        const sectionNames = Object.keys(orderData);
        const promises = [];

        for (const sectionName in orderData) {
            promises.push(saveOrderToServer(sectionName, orderData[sectionName]));
        }

        return promises;
    } catch (error) {
        console.error('Error publishing block order changes:', error);
        return [];
    }
}

export const sortableAPI = {
    initSortable,
    initAllSortables,
    makeBlockDraggable,
    publishBlockOrders
};